import { Flex } from "../../components/Flex";
import { Box } from "../../components/Box";
import { formatFormsTimestamp } from "../../utils/formatters";

import { TableBody } from "../../components/Table/TableBody";
import { TableCell } from "../../components/Table/TableCell";
import { TableHead } from "../../components/Table/TableHead";
import { TableRow } from "../../components/Table/TableRow";
import { CheckStatusEnum } from "../../context/MandateStatusContext";

import {
  CheckHeading,
  CheckTableHeaderRow,
  CheckTableCell,
  Section,
} from "./CheckStyledComponents";
import { CheckResults } from "./CheckResults";
import { GrayCard, Card } from "../../components/Card";
import { Paragraph } from "../../components/Paragraph";
import { Link } from "../../components/Link";
import { Modal } from "../../components/Modal";
import { useState } from "react";
import { FiAlertTriangle, FiCheck, FiX } from "react-icons/fi";
import { FaQuestion } from "react-icons/fa";
import {
  byDateModifiedDesc,
  datesOfBirth,
  orderComments,
  groupAliases,
  hasValue,
  isExcludedMatch,
  matchConflicts,
  orderByDecision,
  orderByScore,
  relationshipSegmentKeys,
  resolveMatchedAlias,
  segmentCounts,
  SEGMENT_LABELS,
  SEGMENT_ORDER,
  uniqueValues,
} from "../../utils/screeningMatch";
import { ExpansionPanel } from "../../components/ExpansionPanel";
import { SideNavTabs } from "../../components/SideNavTabs";
import { H3 } from "../../components/Heading";
import { Button } from "../../components/Button";
import { Tooltip } from "../../components/Tooltip";
import { useUpdateScreeningMatchStatus } from "../../data/useUpdateScreeningMatchStatus";
import { useBulkUpdateScreeningMatchStatus } from "../../data/useBulkUpdateScreeningMatchStatus";
import { useAuth } from "../../context/AuthContext";
import {
  displayError,
  showError,
  showSuccess,
} from "../../utils/error-handling";
import { ApprovalCommentType } from "../../types/Individual";
import {
  formatDate,
  formatSourceDate,
  decidedByName,
  decidedByInitials,
  sourceDomain,
  getSegmentLabel,
  formatIds,
  formatAddresses,
  DECISION_COLORS,
  CHECK_DECISION_LABEL_COLOR,
} from "./screeningResultHelpers";
import {
  DataTable,
  InlineField,
  MatchFlagIcon,
  MATCH_STATUS_ACTIONS,
  SegmentChip,
  AnnotationBadge,
  AliasList,
  FilterableList,
  SourceRow,
  SourceMain,
  NoMatchesFound,
  CheckPending,
  EmptySegment,
  MatchActionButtons,
  SegmentEntry,
  SEGMENT_TABLES,
  StatusPill,
  ScreeningApprovalBanner,
} from "./ScreeningResultPrimitives";
/**
 * A decision drawn in the status pill's colours, as a span so it can sit
 * inside a button.
 */
const DecisionBadge = ({ status, label }: { status: string; label: string }) => {
  const style = DECISION_COLORS[status] || DECISION_COLORS.unknown;
  return (
    <Box
      as="span"
      bg={style.bg}
      color={style.color}
      borderRadius="12px"
      px={2}
      py={1}
      fontSize={0}
      fontWeight="600"
      style={{ whiteSpace: "nowrap", fontFamily: "Inter, sans-serif" }}
    >
      {label}
    </Box>
  );
};

/**
 * Consumer-supplied additions to one match card. Monitoring uses these to mark
 * a match that has evidence the officer has not signed off, and to state its run
 * history. The mandate screening check supplies none and is unchanged.
 */
export interface MatchAnnotation {
  /** Short uppercase flags, e.g. "NEW RESULT". Provenance, not risk category. */
  badges?: string[];
  /** One quiet line under the identity facts, e.g. "4 results · first 04 Mar". */
  note?: string;
}

export interface ScreeningResultViewProps {
  /** `instance.records` from a screening check or a monitoring activity. */
  records: any[];
  /** The owning check or activity status, for the results banner. */
  status?: string;
  /**
   * Overrides the banner label. Monitoring needs it under All activity, where
   * no single check or alert is in scope and there is no status to name.
   */
  statusText?: string;
  /** "entity" suppresses the person-only comparison rows. */
  type?: string;
  /**
   * Fired after a match decision is saved. The view does not know which query
   * supplied its records, so the consumer invalidates its own key.
   */
  onDecisionUpdated?: () => void;
  /** Keyed on `match.id`. */
  annotations?: Record<string | number, MatchAnnotation>;
  /**
   * Rendered at the head of an expanded card, above the profile comparison.
   * Monitoring puts the Evidence strip here, which chooses which run's body the
   * consumer passes down in `records`.
   */
  renderMatchEvidence?: (match: any) => React.ReactNode;
  /**
   * "split" puts Information Used and the results summary side by side,
   * rather than stacked. Mandate screening checks opt into it; Monitoring is
   * unchanged and keeps the default "stacked" layout.
   */
  layout?: "stacked" | "split";
  /**
   * The check-level manual accept/reject decision. Screening-only: rendered
   * inside the tinted decision shell in "split" layout. Ignored in "stacked".
   */
  approvalComment?: ApprovalCommentType;
  /**
   * Drops Section's own left/right padding. The mandate screening check's
   * container has no horizontal padding of its own, so Section supplies it;
   * Monitoring's surrounding Card already does, and without this the two
   * stack, insetting everything ScreeningResultView renders further than the
   * alert panel and the results-lens toggle above it.
   */
  flush?: boolean;
}

/**
 * The rendered body of a LexisNexis screening result: information used, the
 * results summary, and one card per watchlist match.
 *
 * Presentational only, and deliberately so. It takes records rather than an id
 * because the same body has to render a mandate screening check (individual/check)
 * and a monitoring activity (individual/monitoring_activity), which are different
 * endpoints returning the same shape. ScreeningCheck is the fetching wrapper for
 * the first; the Monitoring record view is the second consumer.
 */
export const ScreeningResultView = ({
  records = [],
  status,
  statusText,
  type,
  onDecisionUpdated,
  annotations,
  renderMatchEvidence,
  layout = "stacked",
  approvalComment,
  flush = false,
}: ScreeningResultViewProps) => {
  const isPending = status === CheckStatusEnum.Pending;
  const sectionStyle = flush ? { paddingLeft: 0, paddingRight: 0 } : undefined;
  const [selectedExternalUrl, setSelectedExternalUrl] = useState<string | null>(
    null,
  );

  // excluded matches stay out of view until asked for. Opening or closing
  // them changes no count
  const [showExcluded, setShowExcluded] = useState(false);

  const [statusModalMatch, setStatusModalMatch] = useState<{
    id: number;
    name: string;
    dob: string;
    actionKey: string;
  } | null>(null);

  const {
    mutate: updateScreeningMatchStatus,
    isLoading: isUpdatingMatchStatus,
  } = useUpdateScreeningMatchStatus();

  // the Unknown matches on one record, waiting on the bulk False Positive
  // confirmation. Bulk decisions never span records
  const [bulkFalsePositiveIds, setBulkFalsePositiveIds] = useState<
    number[] | null
  >(null);
  const { hasPermission } = useAuth();
  const canDecide = hasPermission("screening.review_activity");

  const {
    mutate: bulkUpdateScreeningMatchStatus,
    isLoading: isBulkUpdating,
  } = useBulkUpdateScreeningMatchStatus();

  const closeBulkModal = () => setBulkFalsePositiveIds(null);

  const confirmBulkFalsePositive = (comment: string) => {
    if (!bulkFalsePositiveIds?.length) return;
    const total = bulkFalsePositiveIds.length;

    bulkUpdateScreeningMatchStatus(
      {
        // the same comment on every match, so each keeps its own decision
        // entry, exactly as if decided one at a time
        matches: bulkFalsePositiveIds.map((id) => ({
          match_id: id,
          status: "false_positive",
          comment,
        })),
      },
      {
        onSuccess: (response) => {
          const results = response?.data?.results || [];
          const failed = results.filter((result) => result.status === "failed");
          const updated = results.length - failed.length;

          onDecisionUpdated?.();
          closeBulkModal();

          // best-effort: what could be applied was, so say exactly what was
          // not and why, and leave those matches as Unknown to retry
          if (failed.length === 0) {
            showSuccess(
              `${updated} match${updated === 1 ? "" : "es"} marked as False Positive.`,
            );
          } else {
            displayError(
              `${updated} of ${total} marked as False Positive. ${failed.length} could not be updated and remain Unknown: ${Array.from(
                new Set(failed.map((result) => result.detail).filter(Boolean)),
              ).join(" ")}`,
            );
          }
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while updating the matches.");
        },
      },
    );
  };

  const openExternalLinkModal = (url: string) => {
    if (!url) return;
    setSelectedExternalUrl(url);
  };

  const closeExternalLinkModal = () => setSelectedExternalUrl(null);

  const proceedToExternalLink = () => {
    if (selectedExternalUrl) {
      window.open(selectedExternalUrl, "_blank", "noopener,noreferrer");
    }
    closeExternalLinkModal();
  };

  const openStatusModal = (match: any, actionKey: string) => {
    if (!match?.id) return;
    setStatusModalMatch({
      id: match.id,
      name: match?.profile_details?.matched?.name?.full || match?.match_id,
      dob: formatDate(match?.profile_details?.matched?.date_of_birth),
      actionKey,
    });
  };

  const closeStatusModal = () => setStatusModalMatch(null);

  const confirmStatusUpdate = (comment: string) => {
    if (!statusModalMatch) return;
    const action = MATCH_STATUS_ACTIONS[statusModalMatch.actionKey];

    updateScreeningMatchStatus(
      { id: statusModalMatch.id, status: action.status, comment },
      {
        onSuccess: () => {
          onDecisionUpdated?.();
          showSuccess(`Match status updated to ${action.label} successfully.`);
          closeStatusModal();
        },
        onError: (err: any) => {
          showError(
            err,
            "A problem occurred while updating the match status.",
          );
        },
      },
    );
  };

  const checkResultsContent = (
    <>
      {records.length === 0 && (
        <Section style={sectionStyle}>
          {isPending ? (
            <CheckPending type={type} />
          ) : (
            <NoMatchesFound type={type} />
          )}
        </Section>
      )}

      {records.map((record: any, recordIndex: number) => {
        const summary = record?.check_results_summary;

        // a passed check returns one record with no matches, and on that path
        // the provider does not echo back what was searched either
        const matchCount = (record?.matches || []).length;
        const hasInformationUsed = Object.values(
          record?.information_used || {},
        ).some(hasValue);

        const stats = [
          {
            key: "total",
            icon: <FiCheck size={10} strokeWidth={3} />,
            iconBg: "green.50",
            iconColor: "green.primary",
            label: "Results Found",
            value: summary?.total ?? 0,
            subtext: "Identified the presented document",
          },
          {
            key: "unknown",
            icon: <FaQuestion size={7} />,
            iconBg: "gray.20",
            iconColor: "gray.50",
            label: "Unknown",
            value: summary?.unknown ?? 0,
            subtext: "Matches with document",
          },
          // colours match the decision buttons and status pill, so the counts
          // and the individual matches read as the same scheme
          {
            key: "false_positive",
            icon: <FiX size={10} strokeWidth={3} />,
            iconBg: "green.50",
            iconColor: "green.primary",
            label: "False Positive",
            value: summary?.false_positive ?? 0,
            subtext: "Discounted, not the subject",
          },
          {
            key: "true_match",
            icon: <FiAlertTriangle size={10} strokeWidth={2.5} />,
            iconBg: "red.50",
            iconColor: "red.40",
            label: "True Match",
            value: summary?.true_match ?? 0,
            subtext: "Confirmed as the subject",
          },
        ];

        // Screening's split layout leads with severity (True Match first)
        // and folds "Results Found" into the section subtext rather than
        // giving it a box of its own.
        const splitStats = [
          {
            key: "true_match",
            icon: <FiAlertTriangle size={16} strokeWidth={2.5} />,
            iconBg: "red.50",
            iconColor: "red.40",
            boxBg: "red.50",
            labelColor: "red.40",
            label: "True Match",
            value: summary?.true_match ?? 0,
          },
          {
            key: "false_positive",
            icon: <FiX size={16} strokeWidth={3} />,
            iconBg: "green.50",
            iconColor: "green.primary",
            boxBg: "gray.5",
            labelColor: "gray.80",
            label: "False Positive",
            value: summary?.false_positive ?? 0,
          },
          {
            key: "unknown",
            icon: <FaQuestion size={12} />,
            iconBg: "gray.20",
            iconColor: "gray.50",
            boxBg: "gray.5",
            labelColor: "gray.80",
            label: "Unknown",
            value: summary?.unknown ?? 0,
          },
        ];

        const informationUsedFields = [
          type !== "entity"
            ? {
                label: "Date of Birth",
                value: formatDate(record?.information_used?.date_of_birth),
              }
            : null,
          type !== "entity"
            ? { label: "Gender", value: record?.information_used?.gender }
            : null,
          { label: "Type", value: record?.information_used?.type },
        ].filter(Boolean) as { label: string; value: any }[];

        const infoResultsRow = (hasInformationUsed || matchCount > 0) && (
          <Flex gap="16" flexWrap="wrap" alignItems="stretch">
            {hasInformationUsed && (
              <Box flex={1} minWidth="180px">
                <CheckHeading color="gray.50" fontSize={0} mb={2}>
                  INFORMATION USED
                </CheckHeading>
                <Paragraph
                  fontWeight="700"
                  fontSize={2}
                  mb={1}
                  mt={2}
                  color="gray.80"
                >
                  {record?.information_used?.name}
                </Paragraph>
                <Flex gap="24" flexWrap="wrap">
                  {informationUsedFields.map((field) => (
                    <Box key={field.label}>
                      <Paragraph fontSize={0} mb={0} mt={0} color="gray.50">
                        {field.label}
                      </Paragraph>
                      <Paragraph
                        fontSize={1}
                        fontWeight="700"
                        mb={0}
                        mt={0}
                        color="gray.80"
                      >
                        {field.value || "-"}
                      </Paragraph>
                    </Box>
                  ))}
                </Flex>
              </Box>
            )}

            {matchCount > 0 && (
              <Box
                flex={2}
                minWidth="280px"
                {...(hasInformationUsed
                  ? {
                      borderLeft: "1px solid",
                      borderLeftColor: "gray.20",
                      pl: 2,
                    }
                  : {})}
              >
                <Flex
                  justifyContent="space-between"
                  alignItems="baseline"
                  flexWrap="wrap"
                  gap="8"
                  mb={2}
                >
                  <CheckHeading color="gray.50" fontSize={0} mb={0}>
                    CHECK RESULTS
                  </CheckHeading>
                  <Paragraph fontSize={0} mb={0} mt={0} color="gray.50">
                    {summary?.total ?? matchCount} Results Found
                    {/* "· identified the presented document" removed — it
                        isn't backed by anything in the API response, so it
                        was always a fabricated claim, not real data */}
                  </Paragraph>
                </Flex>
                <Flex gap="12" flexWrap="wrap">
                  {splitStats.map((stat) => (
                    <Box
                      key={stat.key}
                      flex={1}
                      minWidth="140px"
                      p={2}
                      borderRadius="8px"
                      bg={stat.boxBg}
                    >
                      <Flex alignItems="center" gap="8">
                        <Box
                          flexShrink={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          height="28px"
                          width="28px"
                          borderRadius="6px"
                          bg={stat.iconBg}
                          color={stat.iconColor}
                          aria-label={stat.label}
                        >
                          {stat.icon}
                        </Box>
                        <Paragraph
                          fontWeight="700"
                          fontSize={1}
                          mb={0}
                          mt={0}
                          color="gray.80"
                        >
                          {stat.value}
                        </Paragraph>
                      </Flex>
                      <Paragraph
                        fontSize={1}
                        fontWeight="600"
                        mb={0}
                        mt={1}
                        color={stat.labelColor}
                      >
                        {stat.label}
                      </Paragraph>
                    </Box>
                  ))}
                </Flex>
              </Box>
            )}
          </Flex>
        );

        const hasDecision = Boolean(
          approvalComment && CHECK_DECISION_LABEL_COLOR[status || ""],
        );

        // a match the provider flagged for exclusion (a non-empty
        // exclude_reason) is pulled out of the normal Screening Matches list
        // and into a collapsed panel beneath it. The main list is the work
        // queue, ordered by decision then score, so a match dismissed as a
        // False Positive drops to the bottom once the record refreshes.
        // Excluded matches carry no decision, so they go by score alone
        const allMatches = record?.matches || [];
        const activeMatches = orderByDecision(
          allMatches.filter((match: any) => !isExcludedMatch(match)),
        );
        const excludedMatches = orderByScore(
          allMatches.filter(isExcludedMatch),
        );
        const hasOnlyExcluded =
          activeMatches.length === 0 && excludedMatches.length > 0;
        // excluded matches carry no decision, so only the main list counts
        const unknownMatchIds: number[] = activeMatches
          .filter((match: any) => (match?.status ?? "unknown") === "unknown")
          .map((match: any) => match?.id)
          .filter((id: any) => id != null);

        return (
          <Section
            key={record?.id ?? recordIndex}
            style={sectionStyle}
            pb={recordIndex < records.length - 1 ? 3 : 0}
          >
            {layout === "split" ? (
              <>
                {/* one neutral bordered box for the banner (once decided)
                    plus Information Used / Check Results — no colour fill,
                    just a divider line to separate the two */}
                {hasDecision && recordIndex === 0 ? (
                  <Box
                    bg="white"
                    border="1px solid"
                    borderColor="gray.20"
                    borderRadius="8px"
                    p={2}
                    mb={2}
                  >
                    <Box
                      pb={2}
                      mb={2}
                      borderBottom="1px solid"
                      borderBottomColor="gray.20"
                    >
                      <ScreeningApprovalBanner
                        data={approvalComment}
                        status={status}
                      />
                    </Box>
                    {infoResultsRow}
                  </Box>
                ) : (
                  infoResultsRow && (
                    <Box
                      bg="white"
                      border="1px solid"
                      borderColor="gray.20"
                      borderRadius="8px"
                      p={2}
                      mb={2}
                    >
                      {infoResultsRow}
                    </Box>
                  )
                )}

                {/* no "Check Results - {status}" line here — the decision
                    is already shown via the banner above and the pill on
                    each match row, so it added a redundant heading that
                    served no purpose */}

                {matchCount === 0 &&
                  (isPending ? (
                    <CheckPending type={type} />
                  ) : (
                    <NoMatchesFound type={type} />
                  ))}

                {activeMatches.length > 0 && (
                  <CheckHeading pb={3}>Screening Matches</CheckHeading>
                )}
              </>
            ) : (
              <>
                {/* what was searched, confirmed before what came back. kept
                    to a single line so the matches start higher up the page */}
                {hasInformationUsed && (
                  <>
                    <H3 mb={2}>Information Used</H3>
                    <GrayCard mx="0px" mb={3} py={2}>
                      <Flex flexWrap="wrap" alignItems="baseline" gap="24">
                        <InlineField
                          label="Name"
                          value={record?.information_used?.name}
                        />
                        {type !== "entity" && (
                          <InlineField
                            label="Date of Birth"
                            value={formatDate(
                              record?.information_used?.date_of_birth,
                            )}
                          />
                        )}
                        {type !== "entity" && (
                          <InlineField
                            label="Gender"
                            value={record?.information_used?.gender}
                          />
                        )}
                        <InlineField
                          label="Type"
                          value={record?.information_used?.type}
                        />
                      </Flex>
                    </GrayCard>
                  </>
                )}

                <CheckResults
                  status={status}
                  flush
                  statusText={
                    statusText ??
                    (status === CheckStatusEnum.Review
                      ? "In Review"
                      : undefined)
                  }
                />

                {/* a passed check needs a plain statement, not a strip of
                    zeroes above an empty list */}
                {matchCount === 0 &&
                  (isPending ? (
                    <CheckPending type={type} />
                  ) : (
                    <NoMatchesFound type={type} />
                  ))}

                {matchCount > 0 && (
                  <>
                    <GrayCard mx="0px" mb={3}>
                      <Flex
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap="16"
                      >
                        {stats.map((stat) => (
                          <Box key={stat.key} flex={1} minWidth="180px">
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              height="20px"
                              width="20px"
                              borderRadius="6px"
                              bg={stat.iconBg}
                              color={stat.iconColor}
                              mb={1}
                              aria-label={stat.label}
                            >
                              {stat.icon}
                            </Box>
                            <Paragraph
                              fontWeight="600"
                              mb={0}
                              mt={0}
                              color="gray.80"
                            >
                              {stat.value} {stat.label}
                            </Paragraph>
                            <Paragraph
                              fontSize={0}
                              mb={0}
                              mt={1}
                              color="gray.50"
                            >
                              {stat.subtext}
                            </Paragraph>
                          </Box>
                        ))}
                      </Flex>
                    </GrayCard>

                    {activeMatches.length > 0 && (
                      <CheckHeading pb={3}>Screening Matches</CheckHeading>
                    )}
                  </>
                )}
              </>
            )}

            {matchCount > 0 && (
              <>
            {hasOnlyExcluded && (
              <GrayCard mx="0px" mb={2}>
                <Paragraph mt={0} mb={0} color="gray.60">
                  No matches need review. Every result for this{" "}
                  {type === "entity" ? "entity" : "individual"} was excluded
                  by your screening preferences.
                </Paragraph>
              </GrayCard>
            )}

            {/* clears the obvious non-matches in one action with one
                comment, typically once the real match has been confirmed */}
            {/* a bar of its own, with the two decisions drawn as the same
                pills the match cards use, so it is plain what will change */}
            {canDecide && unknownMatchIds.length > 0 && (
              <Flex
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap="12"
                mb={2}
                p={2}
                bg="gray.5"
                border="1px solid"
                borderColor="gray.20"
                borderRadius="8px"
              >
                <Paragraph
                  mt={0}
                  mb={0}
                  fontSize={1}
                  fontWeight="600"
                  color="gray.80"
                >
                  {`${unknownMatchIds.length} match${
                    unknownMatchIds.length === 1 ? " is" : "es are"
                  } still Unknown. Dismiss them all in one step, with one comment.`}
                </Paragraph>
                <Button
                  variant="secondary"
                  color="gray.80"
                  fontWeight="600"
                  onClick={() => setBulkFalsePositiveIds(unknownMatchIds)}
                >
                  <Flex as="span" alignItems="center" gap="8">
                    <span>{`Mark all ${unknownMatchIds.length}`}</span>
                    <DecisionBadge status="unknown" label="Unknown" />
                    <span>as</span>
                    <DecisionBadge
                      status="false_positive"
                      label="False Positive"
                    />
                  </Flex>
                </Button>
              </Flex>
            )}

            <Box>
              {activeMatches.map((match: any, matchIndex: number) =>
                renderMatchCard(match, matchIndex),
              )}
            </Box>

            {/* hidden by default: exclusions are not work, but stay to hand
                for a reviewer checking why an expected hit did not appear.
                Shown at the same width as the screening matches, not nested
                inside a panel */}
            {excludedMatches.length > 0 && (
              <Box mt={activeMatches.length > 0 ? 3 : 0}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  gap="16"
                  pb={2}
                >
                  <CheckHeading pb={0}>
                    {`Excluded by your screening preferences (${excludedMatches.length})`}
                  </CheckHeading>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowExcluded((shown) => !shown)}
                  >
                    {showExcluded
                      ? "Hide excluded results"
                      : "Show excluded results"}
                  </Button>
                </Flex>
                <Paragraph mt={0} mb={2} fontSize={1} color="gray.60">
                  These results were excluded automatically by your account's
                  screening preferences. They do not need to be reviewed, and
                  they do not affect this record's status or appear on its
                  report. They are shown here for reference, in case you want
                  to check them or adjust your screening preferences.
                </Paragraph>
                {showExcluded && (
                  <Box>
                    {excludedMatches.map((match: any, matchIndex: number) =>
                      renderMatchCard(match, matchIndex),
                    )}
                  </Box>
                )}
              </Box>
            )}
              </>
            )}
          </Section>
        );

        function renderMatchCard(match: any, matchIndex: number) {
                const submitted = match?.profile_details?.submitted;
                const matched = match?.profile_details?.matched;
                const matchFlags = match?.profile_details?.match || {};

                // every category is rendered, including empty ones: "no
                // sanctions exposure" is a finding, and hiding the category
                // makes the reviewer prove a negative
                const segmentEntries = SEGMENT_ORDER.map((key) => [
                  key,
                  byDateModifiedDesc(
                    Array.isArray(match?.segments?.[key])
                      ? match.segments[key]
                      : [],
                  ),
                ]) as [string, any[]][];
                const relationships = match?.relationships || [];
                // latest first, so the most recent listing or report is what a
                // reviewer sees before scrolling
                const comments = orderComments(match?.comments);
                const resources = byDateModifiedDesc(match?.resources);
                const excludeReasons: string[] = Array.isArray(
                  match?.exclude_reason,
                )
                  ? match.exclude_reason
                  : [];
                // a decision on an excluded match has no effect on the record
                // status or the PDF, so none is offered; one already made is
                // still shown, read-only, through the decision line
                const isExcluded = isExcludedMatch(match);

                // the alias a match was probably reached through. two of the
                // three matches in the reference payload are nickname-only
                // hits, and are inexplicable without this
                const matchedAlias = resolveMatchedAlias(
                  record?.information_used?.name,
                  matched,
                );
                const conflicts = matchConflicts(matchFlags);
                const chips = segmentCounts(match?.segments);
                const aliasGroups = groupAliases(matched?.aka);
                const decidedBy = decidedByName(match?.decision?.decided_by);

                // The Match column has three states, not two. The provider's
                // flags record whether a *conflict* was found, so a flag of
                // true on a field that was never submitted means "nothing to
                // conflict with", not "verified". Only entity type, name, date
                // of birth and gender are ever submitted, so a tick against
                // identifiers, addresses or nationality would claim a check
                // that did not happen.
                const comparisonRows = [
                  {
                    label: "Entity Type",
                    submittedValue: submitted?.entity_type,
                    matchedValue: matched?.entity_type,
                    matched: matchFlags?.entity_type,
                    compared: hasValue(submitted?.entity_type),
                  },
                  {
                    label: "Name",
                    submittedValue: submitted?.name?.full,
                    matchedValue: matched?.name?.full,
                    matched: matchFlags?.name,
                    compared: hasValue(submitted?.name?.full),
                  },
                  ...(type !== "entity"
                    ? [
                        {
                          label: "Date of Birth",
                          submittedValue: formatDate(submitted?.date_of_birth),
                          matchedValue: datesOfBirth(
                            matched?.date_of_birth,
                          ).join(", "),
                          matched: matchFlags?.date_of_birth,
                          compared: hasValue(submitted?.date_of_birth),
                        },
                        {
                          label: "Gender",
                          submittedValue: submitted?.gender,
                          matchedValue: matched?.gender,
                          matched: matchFlags?.gender,
                          compared: hasValue(submitted?.gender),
                        },
                      ]
                    : []),
                  {
                    label: "ID Numbers",
                    submittedValue: formatIds(submitted?.ids).join(", "),
                    matchedValue: formatIds(matched?.ids).join(", "),
                    matched: matchFlags?.ids,
                    compared: hasValue(submitted?.ids),
                  },
                  {
                    label: "Address",
                    submittedValue: formatAddresses(submitted?.addresses).join(
                      ", ",
                    ),
                    matchedValue: formatAddresses(matched?.addresses).join(
                      ", ",
                    ),
                    matched: matchFlags?.addresses,
                    compared: hasValue(submitted?.addresses),
                  },
                  ...(type !== "entity"
                    ? [
                        {
                          label: "Nationality",
                          submittedValue: uniqueValues(
                            submitted?.nationality,
                          ).join(", "),
                          matchedValue: uniqueValues(
                            matched?.nationality,
                          ).join(", "),
                          matched: matchFlags?.nationality,
                          compared: hasValue(submitted?.nationality),
                        },
                        {
                          label: "Place Of Birth",
                          submittedValue: uniqueValues(
                            submitted?.place_of_birth,
                          ).join(", "),
                          matchedValue: uniqueValues(
                            matched?.place_of_birth,
                          )[0],
                          // the provider returns no place-of-birth flag at all,
                          // so this row can never be a comparison
                          matched: undefined,
                          compared: false,
                        },
                      ]
                    : []),
                ];

                const identityFacts =
                  type === "entity"
                    ? [
                        matched?.entity_type,
                        uniqueValues(matched?.nationality)[0],
                      ]
                    : [
                        datesOfBirth(matched?.date_of_birth)[0] ||
                          "No date of birth",
                        uniqueValues(matched?.nationality).join(", ") ||
                          "No nationality",
                        uniqueValues(matched?.place_of_birth)[0],
                      ];

                const flaggedFor = chips.filter((chip) => chip.count > 0);
                const annotation = annotations?.[match?.id];

                const rowTitle = (
                  <Box width="100%">
                    <Flex alignItems="flex-start" gap="16" width="100%">
                      <Box flex={1}>
                        {/* segment/provenance chips above the name, as
                            before — wraps onto additional lines on a narrow
                            (tablet) viewport instead of overflowing */}
                        {(flaggedFor.length > 0 ||
                          !!annotation?.badges?.length) && (
                          <Flex gap="4" mb={1} flexWrap="wrap">
                            {(annotation?.badges || []).map((badge) => (
                              <AnnotationBadge key={badge}>
                                {badge}
                              </AnnotationBadge>
                            ))}
                            {flaggedFor.map((chip) => (
                              <SegmentChip key={chip.key}>
                                {chip.label}
                              </SegmentChip>
                            ))}
                          </Flex>
                        )}

                        {/* name and alias share one wrapping row */}
                        <Flex alignItems="center" gap="8" flexWrap="wrap" mb={1}>
                          <Paragraph
                            fontWeight="600"
                            mb={0}
                            mt={0}
                            color="gray.80"
                          >
                            {matched?.name?.full || match?.match_id}
                          </Paragraph>

                          {matchedAlias && (
                            <Flex alignItems="center" gap="4" flexWrap="wrap">
                              <Box
                                bg="orange.primary"
                                color="orange.50"
                                borderRadius="4px"
                                px={1}
                                fontSize={0}
                                fontWeight="600"
                                letterSpacing="0.04em"
                                fontFamily="Inter, sans-serif"
                              >
                                ALIAS
                              </Box>
                              <Paragraph
                                mb={0}
                                mt={0}
                                fontSize={1}
                                color="gray.80"
                                fontWeight="500"
                              >
                                &ldquo;{matchedAlias.full}&rdquo;
                                {matchedAlias.type ? (
                                  <Box
                                    as="span"
                                    color="gray.50"
                                    fontWeight="400"
                                  >{` (${matchedAlias.type})`}</Box>
                                ) : null}
                              </Paragraph>
                            </Flex>
                          )}
                        </Flex>

                        {/* a div rather than a p, so the score's tooltip can
                            sit inside it */}
                        <Paragraph
                          as="div"
                          fontSize={0}
                          mb={0}
                          mt={1}
                          color="gray.50"
                        >
                          {identityFacts.filter(Boolean).join("  ·  ")}
                          {matched?.date_listed
                            ? `  ·  Listed ${formatDate(matched.date_listed)}`
                            : ""}
                          {/* named as LexisNexis's score so it is not read as
                              the fuzzy name sensitivity in the tenant's
                              screening preferences, a different measure */}
                          {match?.entityScore != null && (
                            <>
                              {"  ·  "}
                              <Tooltip
                                position="top"
                                content="LexisNexis's overall match score for this entry. Separate from the fuzzy name sensitivity in your screening preferences."
                              >
                                <Box
                                  as="span"
                                  borderBottom="1px dotted"
                                  borderBottomColor="gray.50"
                                >
                                  LexisNexis score {match.entityScore}
                                </Box>
                              </Tooltip>
                            </>
                          )}
                        </Paragraph>

                        {annotation?.note && (
                          <Paragraph fontSize={0} mb={0} mt={1} color="gray.50">
                            {annotation.note}
                          </Paragraph>
                        )}

                        {conflicts.length > 0 && (
                          <Paragraph
                            fontSize={0}
                            mb={0}
                            mt={1}
                            color="red.primary"
                          >
                            Conflicts: {conflicts.join(", ")}
                          </Paragraph>
                        )}

                        {/* who decided this match, why, and when — shown
                            only once a user has actually decided it, since a
                            freshly created record carries no decider. One
                            compact line rather than a card, so it doesn't
                            compete for space with the identity details above */}
                        {decidedBy &&
                          (() => {
                            const decisionStyle =
                              DECISION_COLORS[match?.status || ""] ||
                              DECISION_COLORS.unknown;
                            return (
                              <Flex
                                alignItems="center"
                                gap="8"
                                mt={1}
                                pl={2}
                                borderLeft="2px solid"
                                borderLeftColor={decisionStyle.color}
                              >
                                <Box
                                  flexShrink={0}
                                  width="20px"
                                  height="20px"
                                  borderRadius="50%"
                                  bg={decisionStyle.bg}
                                  color={decisionStyle.color}
                                  fontSize={0}
                                  fontWeight="700"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  fontFamily="Inter, sans-serif"
                                >
                                  {decidedByInitials(
                                    match?.decision?.decided_by,
                                  )}
                                </Box>
                                {match?.decision?.comment && (
                                  <Paragraph
                                    fontSize={1}
                                    mb={0}
                                    mt={0}
                                    color="gray.80"
                                    style={{
                                      flexGrow: 0,
                                      flexShrink: 1,
                                      minWidth: 0,
                                      maxWidth: "70%",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    &ldquo;{match.decision.comment}&rdquo;
                                  </Paragraph>
                                )}
                                <Paragraph
                                  fontSize={0}
                                  mb={0}
                                  mt={0}
                                  color="gray.50"
                                  style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                                >
                                  {decidedByName(match?.decision?.decided_by)}
                                  {match?.decision?.decided_at
                                    ? ` · ${formatFormsTimestamp(match.decision.decided_at)}`
                                    : ""}
                                </Paragraph>
                              </Flex>
                            );
                          })()}

                        {/* the provider's reasons for flagging this match for
                            exclusion, shown the same way as the decision line
                            above — a compact bordered row rather than a tab a
                            reviewer would have to open to find out why a
                            match landed in Excludes */}
                        {excludeReasons.length > 0 && (
                          <Flex
                            alignItems="center"
                            gap="8"
                            mt={1}
                            pl={2}
                            borderLeft="2px solid"
                            borderLeftColor="orange.50"
                          >
                            <Paragraph
                              fontSize={1}
                              mb={0}
                              mt={0}
                              color="gray.80"
                            >
                              <Box
                                as="span"
                                fontWeight="600"
                                color="orange.50"
                              >
                                Excluded reasons:
                              </Box>{" "}
                              {excludeReasons.join(", ")}
                              <Box as="span" color="gray.50">
                                {"  ·  "}No review needed
                              </Box>
                            </Paragraph>
                          </Flex>
                        )}
                      </Box>

                      {!isExcluded && (
                        <>
                          <Flex
                            width="130px"
                            flexShrink={0}
                            justifyContent="flex-end"
                          >
                            <StatusPill
                              decision={match?.decision}
                              status={match?.status}
                              statusDisplay={match?.status_display}
                            />
                          </Flex>
                          <MatchActionButtons
                            currentStatus={match?.status}
                            onSelect={(actionKey) =>
                              openStatusModal(match, actionKey)
                            }
                          />
                        </>
                      )}
                    </Flex>
                  </Box>
                );

                const profileDetailsTitle = (
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Paragraph fontWeight="500" color="darkBlue">
                      Profile Details
                    </Paragraph>
                    <Paragraph color="gray.50" mb={0} mt={0}>
                      {matched?.list_reference_number
                        ? `ID ${matched.list_reference_number}`
                        : ""}
                    </Paragraph>
                  </Flex>
                );

                const segmentTabs = segmentEntries.map(
                  ([category, entries]: [string, any]) => {
                    const SegmentTable = SEGMENT_TABLES[category];
                    return {
                      key: category,
                      label: getSegmentLabel(category),
                      count: entries.length,
                      content: !entries.length ? (
                        <EmptySegment label={getSegmentLabel(category)} />
                      ) : SegmentTable ? (
                        <SegmentTable entries={entries} />
                      ) : (
                        entries.map((entry: any, i: number) => (
                          <SegmentEntry key={i} entry={entry} />
                        ))
                      ),
                    };
                  },
                );

                const supportingTabs = [
                  {
                    key: "aliases",
                    label: "Aliases",
                    count: aliasGroups.aliases.length,
                    content: aliasGroups.aliases.length ||
                    aliasGroups.transliterations.length ? (
                      <AliasList
                        aliases={aliasGroups.aliases}
                        transliterations={aliasGroups.transliterations}
                        matchedAlias={matchedAlias}
                      />
                    ) : (
                      <EmptySegment label="Alias" />
                    ),
                  },

                  {
                    key: "relationships",
                    label: "Relationships",
                    count: relationships.length,
                    content: !relationships.length ? (
                      <EmptySegment label="Relationship" />
                    ) : (
                      <FilterableList
                        items={relationships}
                        placeholder="Filter by name, relationship or group"
                        toText={(relation) =>
                          [
                            relation?.entity_name,
                            relation?.group,
                            relation?.type,
                            relation?.segments,
                          ]
                            .filter(Boolean)
                            .join(" ")
                        }
                      >
                        {(visible) => (
                          <DataTable>
                            <TableHead></TableHead>
                            <TableBody>
                              <CheckTableHeaderRow>
                                <CheckTableCell>Entity Name</CheckTableCell>
                                <CheckTableCell>Group</CheckTableCell>
                                <CheckTableCell>Relationship</CheckTableCell>
                                <CheckTableCell>Ownership</CheckTableCell>
                                <CheckTableCell>Flagged For</CheckTableCell>
                              </CheckTableHeaderRow>
                              {visible.map((relation: any, i: number) => (
                                <TableRow key={i}>
                                  <TableCell
                                    color="gray.80"
                                    borderBottom="none"
                                    verticalAlign="top"
                                  >
                                    {relation?.entity_name || "-"}
                                  </TableCell>
                                  <TableCell
                                    color="gray.60"
                                    borderBottom="none"
                                    verticalAlign="top"
                                  >
                                    {relation?.group || "-"}
                                  </TableCell>
                                  <TableCell
                                    color="gray.60"
                                    borderBottom="none"
                                    verticalAlign="top"
                                  >
                                    {relation?.type || "-"}
                                  </TableCell>
                                  <TableCell
                                    color="gray.60"
                                    borderBottom="none"
                                    verticalAlign="top"
                                  >
                                    {relation?.ownership_percentage
                                      ? `${relation.ownership_percentage}%`
                                      : "-"}
                                  </TableCell>
                                  <TableCell
                                    color="gray.60"
                                    borderBottom="none"
                                    verticalAlign="top"
                                  >
                                    {/* an associate who is themselves
                                        sanctioned is the most useful thing
                                        on this row */}
                                    <Flex gap="4" flexWrap="wrap">
                                      {relationshipSegmentKeys(
                                        relation?.segments,
                                      ).map((key) => (
                                        <SegmentChip key={key}>
                                          {SEGMENT_LABELS[key]}
                                        </SegmentChip>
                                      ))}
                                    </Flex>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </DataTable>
                        )}
                      </FilterableList>
                    ),
                  },

                  {
                    key: "comments",
                    label: "Comments",
                    count: comments.length,
                    content: !comments.length ? (
                      <EmptySegment label="Comment" />
                    ) : (
                      <FilterableList
                        items={comments}
                        placeholder="Filter comments"
                        toText={(comment) =>
                          [comment?.source, comment?.date, comment?.text]
                            .filter(Boolean)
                            .join(" ")
                        }
                      >
                        {(visible) =>
                          visible.map((comment: any, i: number) => {
                            // a missing source, date or body is omitted rather
                            // than filled with a placeholder
                            const source = comment?.source?.trim?.() || "";
                            const date = comment?.date?.trim?.() || "";
                            const text = comment?.text?.trim?.() || "";
                            if (!source && !date && !text) return null;

                            return (
                              <Box
                                key={i}
                                mb={i === visible.length - 1 ? 0 : 3}
                              >
                                {(source || date) && (
                                  <Flex
                                    justifyContent="space-between"
                                    alignItems="baseline"
                                    gap="16"
                                  >
                                    {source && (
                                      <Paragraph
                                        mb={0}
                                        mt={0}
                                        fontSize={0}
                                        fontWeight="600"
                                        color="gray.60"
                                      >
                                        {source}
                                      </Paragraph>
                                    )}
                                    {date && (
                                      <Paragraph
                                        color="gray.50"
                                        fontSize={0}
                                        mb={0}
                                        mt={0}
                                        style={{ whiteSpace: "nowrap" }}
                                      >
                                        {date}
                                      </Paragraph>
                                    )}
                                  </Flex>
                                )}
                                {text && (
                                  <Paragraph mt={1} mb={0} color="gray.80">
                                    {text}
                                  </Paragraph>
                                )}
                              </Box>
                            );
                          })
                        }
                      </FilterableList>
                    ),
                  },

                  {
                    key: "sources",
                    label: "Sources",
                    count: resources.length,
                    content: !resources.length ? (
                      <EmptySegment label="Source" />
                    ) : (
                      <FilterableList
                        items={resources}
                        placeholder="Filter sources"
                        toText={(resource) =>
                          typeof resource === "string"
                            ? resource
                            : resource?.source_uri || resource?.url || ""
                        }
                      >
                        {(visible) =>
                          visible.map((resource: any, i: number) => {
                            const url =
                              typeof resource === "string"
                                ? resource
                                : resource?.source_uri || resource?.url;
                            if (!url) return null;

                            return (
                              <SourceRow key={i}>
                                {/* the domain says whether a source is
                                    credible, the full URL says which page.
                                    both are needed, so both are shown */}
                                <SourceMain>
                                  <Link
                                    type="button"
                                    color="blue.primary"
                                    onClick={() => openExternalLinkModal(url)}
                                  >
                                    {sourceDomain(url)}
                                  </Link>
                                  <Paragraph
                                    color="gray.50"
                                    fontSize={0}
                                    mt={0}
                                    mb={0}
                                    title={url}
                                    ellipsisOverflow
                                  >
                                    {url}
                                  </Paragraph>
                                </SourceMain>
                                <Paragraph
                                  color="gray.50"
                                  fontSize={0}
                                  mb={0}
                                  mt={0}
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {formatSourceDate(resource?.date_modified)}
                                </Paragraph>
                              </SourceRow>
                            );
                          })
                        }
                      </FilterableList>
                    ),
                  },
                ];

                return (
                  // one card per match, separated by space rather than a rule,
                  // so each is obviously its own unit of review
                  <Card key={match?.id ?? matchIndex} p={3} mb={2}>
                    <ExpansionPanel title={rowTitle}>
                      <Box mt={2}>
                        {/* which run's evidence is on screen. sits above
                            everything the run supplies, and below the decision
                            controls, which belong to the match and not to any
                            one run */}
                        {renderMatchEvidence?.(match)}

                        {/* profile details are the reason the card was opened,
                            so they are visible immediately rather than behind a
                            second click */}
                        <Box
                          borderTop="1px solid"
                          borderTopColor="gray.20"
                          pt={2}
                          mb={3}
                        >
                          <Box mb={2}>{profileDetailsTitle}</Box>
                          <Box>
                            {/* the field name owns a column of its own, rather
                                than being repeated inside both value cells */}
                            <DataTable>
                              <TableHead></TableHead>
                              <TableBody>
                                <CheckTableHeaderRow>
                                  <CheckTableCell style={{ width: "18%" }}>
                                    Field
                                  </CheckTableCell>
                                  <CheckTableCell>
                                    Submitted Details
                                  </CheckTableCell>
                                  <CheckTableCell>
                                    Matched Details
                                  </CheckTableCell>
                                  <CheckTableCell style={{ width: "110px" }}>
                                    Match
                                  </CheckTableCell>
                                </CheckTableHeaderRow>
                                {comparisonRows.map((row) => (
                                  <TableRow key={row.label}>
                                    <TableCell
                                      color="gray.50"
                                      verticalAlign="top"
                                    >
                                      {row.label}
                                    </TableCell>
                                    <TableCell
                                      color="gray.80"
                                      verticalAlign="top"
                                    >
                                      {row.submittedValue || "-"}
                                    </TableCell>
                                    <TableCell
                                      color="gray.80"
                                      verticalAlign="top"
                                    >
                                      {row.matchedValue || "-"}
                                    </TableCell>
                                    <TableCell verticalAlign="top">
                                      {row.compared ? (
                                        <MatchFlagIcon matched={row.matched} />
                                      ) : (
                                        <Paragraph
                                          mt={0}
                                          mb={0}
                                          fontSize={0}
                                          color="gray.40"
                                        >
                                          Not compared
                                        </Paragraph>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </DataTable>
                          </Box>
                        </Box>

                        {/* a vertical menu rather than tab strips: ten items
                            never fit on one line, and a list keeps every
                            option and its count readable while leaving the
                            full width to the tables */}
                        <SideNavTabs
                          groups={[
                            {
                              key: "segments",
                              label: "Why this match is flagged",
                              description: "Select a category to view its records",
                              items: segmentTabs,
                            },
                            {
                              key: "supporting",
                              label: "Supporting information",
                              items: supportingTabs,
                            },
                          ]}
                        />
                      </Box>
                    </ExpansionPanel>
                  </Card>
                );
        }
      })}
    </>
  );

  const externalLinkModal = (
    <Modal
      isOpen={!!selectedExternalUrl}
      onClose={closeExternalLinkModal}
      onModalConfirmClick={proceedToExternalLink}
      title="Open source url in a new tab"
      content={
        selectedExternalUrl
          ? `This source url will open in a new browser tab.
You'll stay on the source page, so you can come back anytime.`
          : ""
      }
      primaryButtonText="Continue"
      secondaryButtonText="Cancel"
      hasSecondaryButton
      primaryButtonVariant="primary"
      secondaryButtonVariant="text"
      width="420px"
    />
  );

  const statusUpdateModal = (
    <Modal
      isOpen={!!statusModalMatch}
      onClose={closeStatusModal}
      onModalConfirmClick={confirmStatusUpdate}
      isClosingDisabled={isUpdatingMatchStatus}
      title={`Are you sure you want to mark this profile with ${
        statusModalMatch
          ? MATCH_STATUS_ACTIONS[statusModalMatch.actionKey].label
          : ""
      } status?`}
      content={
        statusModalMatch
          ? [statusModalMatch.name, statusModalMatch.dob]
              .filter(Boolean)
              .join(" • ")
          : ""
      }
      hasSecondaryButton
      secondaryButtonText="Cancel"
      primaryButtonText="Submit"
      primaryButtonVariant="primary"
      hasCommentBox
      width="450px"
    />
  );

  const bulkCount = bulkFalsePositiveIds?.length ?? 0;
  const bulkFalsePositiveModal = (
    <Modal
      isOpen={bulkCount > 0}
      onClose={closeBulkModal}
      onModalConfirmClick={confirmBulkFalsePositive}
      isClosingDisabled={isBulkUpdating}
      title={`Mark all ${bulkCount} Unknown match${
        bulkCount === 1 ? "" : "es"
      } as False Positive?`}
      content="Your comment will be recorded against each match, with its own entry in the decision history."
      hasSecondaryButton
      secondaryButtonText="Cancel"
      primaryButtonText="Submit"
      primaryButtonVariant="primary"
      hasCommentBox
      width="450px"
    />
  );

  return (
    <>
      {checkResultsContent}
      {externalLinkModal}
      {statusUpdateModal}
      {bulkFalsePositiveModal}
    </>
  );
};
